import { currencyService } from './currencyService';

export type SubscriptionInterval = 'monthly' | 'quarterly' | 'yearly';
export type SubscriptionPaymentMode = 'one_time' | 'recurring';
export type SubscriptionStatus =
  | 'trial'
  | 'active'
  | 'paused'
  | 'past_due'
  | 'canceled'
  | 'expired';
export type SubscriptionBillingStatus = 'scheduled' | 'paid' | 'overdue' | 'unpaid';
export type SubscriptionServiceStatus =
  | 'pending_setup'
  | 'onboarding'
  | 'active_delivery'
  | 'delivered'
  | 'paused'
  | 'closed';

export interface AdminSubscription {
  id: string;
  subscriptionNumber: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientCompany: string;
  serviceId: string;
  serviceName: string;
  paymentMode: SubscriptionPaymentMode;
  paymentModeSource: string;
  amount: number;
  currencyCode: string;
  interval: SubscriptionInterval;
  status: SubscriptionStatus;
  billingStatus: SubscriptionBillingStatus;
  serviceStatus: SubscriptionServiceStatus;
  startDate: string;
  nextBillingDate: string;
  lastBillingDate: string;
  endDate: string;
  linkedOrderNumber: string;
  paymentLinkKey: string;
  mollieCustomerId: string;
  mollieSubscriptionId: string;
  recurringInterval: string;
  recurringStatus: string;
  recurringActivatedAt: string;
  notes: string;
  internalNotes: string;
  updatedAt: string;
}

const ENDPOINT = '/subscriptionManagement.php';

let subscriptionsCache: AdminSubscription[] | null = null;

const parseResponse = async <T,>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || 'Erreur gestion abonnements');
  }

  return payload as T;
};

const generateRecordId = (prefix: string) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID()}`;
  }

  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const padSequence = (value: number) => String(value).padStart(4, '0');

const generateSubscriptionNumber = () => {
  const now = new Date();
  const year = now.getFullYear();
  const sequence = Number(String(now.getTime()).slice(-4));
  return `SUB-${year}-${padSequence(sequence)}`;
};

const normalizeDate = (value?: string | null, fallback = '') => {
  if (!value) {
    return fallback;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return fallback;
  }

  return parsed.toISOString();
};

const sortSubscriptions = (subscriptions: AdminSubscription[]) =>
  [...subscriptions].sort(
    (left, right) =>
      new Date(right.nextBillingDate || right.updatedAt).getTime() -
      new Date(left.nextBillingDate || left.updatedAt).getTime()
  );

const sanitizeSubscription = (subscription: AdminSubscription): AdminSubscription => ({
  id: subscription.id || generateRecordId('subscription'),
  subscriptionNumber:
    subscription.subscriptionNumber?.trim() || generateSubscriptionNumber(),
  clientId: subscription.clientId?.trim() || '',
  clientName: subscription.clientName?.trim() || '',
  clientEmail: subscription.clientEmail?.trim().toLowerCase() || '',
  clientPhone: subscription.clientPhone?.trim() || '',
  clientCompany: subscription.clientCompany?.trim() || '',
  serviceId: subscription.serviceId?.trim() || '',
  serviceName: subscription.serviceName?.trim() || '',
  paymentMode:
    String(subscription.paymentMode || '')
      .toLowerCase()
      .trim() === 'one_time'
      ? 'one_time'
      : 'recurring',
  paymentModeSource: String(subscription.paymentModeSource || '').trim() || 'manual',
  amount: Number(subscription.amount || 0),
  currencyCode: subscription.currencyCode?.trim().toUpperCase() || 'EUR',
  interval: subscription.interval || 'monthly',
  status: subscription.status || 'trial',
  billingStatus: subscription.billingStatus || 'scheduled',
  serviceStatus: subscription.serviceStatus || 'pending_setup',
  startDate: normalizeDate(subscription.startDate, new Date().toISOString()),
  nextBillingDate: normalizeDate(subscription.nextBillingDate),
  lastBillingDate: normalizeDate(subscription.lastBillingDate),
  endDate: normalizeDate(subscription.endDate),
  linkedOrderNumber: subscription.linkedOrderNumber?.trim() || '',
  paymentLinkKey: subscription.paymentLinkKey?.trim() || '',
  mollieCustomerId: subscription.mollieCustomerId?.trim() || '',
  mollieSubscriptionId: subscription.mollieSubscriptionId?.trim() || '',
  recurringInterval: subscription.recurringInterval?.trim() || '',
  recurringStatus: subscription.recurringStatus?.trim() || '',
  recurringActivatedAt: normalizeDate(subscription.recurringActivatedAt),
  notes: subscription.notes?.trim() || '',
  internalNotes: subscription.internalNotes?.trim() || '',
  updatedAt: new Date().toISOString(),
});

const readStore = async (forceRefresh = false): Promise<AdminSubscription[]> => {
  if (!forceRefresh && subscriptionsCache !== null) {
    return subscriptionsCache;
  }

  const response = await fetch(ENDPOINT, {
    credentials: 'include',
    cache: 'no-store',
  });

  const payload = await parseResponse<{ subscriptions: AdminSubscription[] }>(response);
  subscriptionsCache = sortSubscriptions(
    Array.isArray(payload.subscriptions)
      ? payload.subscriptions.map((subscription) => sanitizeSubscription(subscription))
      : []
  );

  return subscriptionsCache;
};

const writeStore = async (subscriptions: AdminSubscription[]) => {
  const response = await fetch(ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      subscriptions: subscriptions.map((subscription) =>
        sanitizeSubscription(subscription)
      ),
    }),
  });

  const payload = await parseResponse<{
    ok: true;
    subscriptions: AdminSubscription[];
  }>(response);
  subscriptionsCache = sortSubscriptions(
    Array.isArray(payload.subscriptions)
      ? payload.subscriptions.map((subscription) => sanitizeSubscription(subscription))
      : []
  );

  return subscriptionsCache;
};

export const createEmptySubscription = (): AdminSubscription => ({
  id: generateRecordId('subscription'),
  subscriptionNumber: generateSubscriptionNumber(),
  clientId: '',
  clientName: '',
  clientEmail: '',
  clientPhone: '',
  clientCompany: '',
  serviceId: '',
  serviceName: '',
  paymentMode: 'recurring',
  paymentModeSource: 'manual',
  amount: 0,
  currencyCode: currencyService.getDisplayCurrencyCode(),
  interval: 'monthly',
  status: 'trial',
  billingStatus: 'scheduled',
  serviceStatus: 'pending_setup',
  startDate: new Date().toISOString(),
  nextBillingDate: '',
  lastBillingDate: '',
  endDate: '',
  linkedOrderNumber: '',
  paymentLinkKey: '',
  mollieCustomerId: '',
  mollieSubscriptionId: '',
  recurringInterval: '',
  recurringStatus: '',
  recurringActivatedAt: '',
  notes: '',
  internalNotes: '',
  updatedAt: new Date().toISOString(),
});

export async function getSubscriptions(
  forceRefresh = false
): Promise<AdminSubscription[]> {
  return readStore(forceRefresh);
}

export async function saveSubscription(
  subscription: AdminSubscription
): Promise<AdminSubscription> {
  const sanitized = sanitizeSubscription(subscription);

  if (
    !sanitized.subscriptionNumber ||
    !sanitized.clientName ||
    !sanitized.clientEmail
  ) {
    throw new Error('Numero d abonnement, client et email sont obligatoires.');
  }

  const subscriptions = await readStore(true);
  const nextSubscriptions = subscriptions.some(
    (entry) => entry.id === sanitized.id
  )
    ? subscriptions.map((entry) =>
        entry.id === sanitized.id ? sanitized : entry
      )
    : [sanitized, ...subscriptions];

  await writeStore(nextSubscriptions);
  return sanitized;
}

export async function removeSubscription(subscriptionId: string): Promise<void> {
  const subscriptions = await readStore(true);
  const nextSubscriptions = subscriptions.filter(
    (subscription) => subscription.id !== subscriptionId
  );
  await writeStore(nextSubscriptions);
}

export const adminSubscriptionService = {
  getSubscriptions,
  saveSubscription,
  removeSubscription,
  createEmptySubscription,
};
